import { Button, Form, Input, InputNumber, message, Table } from "antd";
import { doc, onSnapshot, setDoc, collection } from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { FinanceEntryType } from "../typeDefs";
import { isInGstRegisteredPeriod } from "./taxConfig";

type TaxYearAdjustment = {
  id: string;
  taxYear: string;
  startDate: number;
  endDate: number;
  revenue: number;
  expenses: number;
  assetPurchases: number;
  estimatedProfit: number;
  estimatedCompanyTax: number;
  gstCollected: number;
  gstClaimable: number;
  gstPosition: number;
  openingPosition: number;
  carryOverLoss: number;
  gstCarryOver: number;
  taxPaid: number;
  provisionalTaxPaid: number;
  accountantAdjustments: number;
  finalPosition: number;
  notes?: string;
  updatedAt: number;
};

const COMPANY_TAX_RATE = 0.28;
const money = (value: number) => `$${(value || 0).toFixed(2)}`;
const gstPortion = (amount: number) => (amount * 3) / 23;

const getGstAmount = (entry: FinanceEntryType) => {
  if (!isInGstRegisteredPeriod(entry.date) || !entry.gstIncluded || !entry.gstRegistered) return 0;
  const gst = gstPortion(entry.amount);
  if (entry.category === "Expenses" || entry.category === "Assets") {
    const apportionRate = entry.apportionMethod === "None" ? 0 : entry.apportionRate ?? 100;
    return gst * (apportionRate / 100);
  }
  return gst;
};

const getTaxYearEnd = (date: number) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = d.getMonth();
  return month >= 3 ? year + 1 : year;
};

const getTaxYearRange = (endYear: number) => ({
  id: `${endYear - 1}-${endYear}`,
  taxYear: `${endYear - 1}/${endYear}`,
  startDate: new Date(`${endYear - 1}-04-01`).getTime(),
  endDate: new Date(`${endYear}-04-01`).getTime(),
});

export const TaxYearAdjustments = ({ data }: { data: FinanceEntryType[] }) => {
  const [adjustments, setAdjustments] = useState<Record<string, TaxYearAdjustment>>({});
  const [editing, setEditing] = useState<string | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "taxYearAdjustments"), (snapshot) => {
      const next: Record<string, TaxYearAdjustment> = {};
      snapshot.docs.forEach((item) => {
        next[item.id] = { ...item.data(), id: item.id } as TaxYearAdjustment;
      });
      setAdjustments(next);
    });
    return unsub;
  }, []);

  const generatedYears = useMemo(() => {
    const active = data.filter((item) => !item.isDeleted);
    const firstDate = active.length
      ? Math.min(...active.map((item) => item.date))
      : new Date().getTime();
    const firstEndYear = getTaxYearEnd(firstDate);
    const currentEndYear = getTaxYearEnd(new Date().getTime());

    return Array.from({ length: currentEndYear - firstEndYear + 1 }, (_item, idx) =>
      getTaxYearRange(firstEndYear + idx),
    );
  }, [data]);

  const calculateYear = (year: ReturnType<typeof getTaxYearRange>, previousFinal = 0) => {
    const entries = data
      .filter((item) => !item.isDeleted)
      .filter((item) => item.date >= year.startDate && item.date < year.endDate);

    const revenue = entries
      .filter((item) => item.category === "Revenue")
      .reduce((total, item) => total + item.amount, 0);
    const expenses = entries
      .filter((item) => item.category === "Expenses")
      .reduce((total, item) => total + item.amount, 0);
    const assetPurchases = entries
      .filter((item) => item.category === "Assets")
      .reduce((total, item) => total + item.amount, 0);
    const estimatedProfit = revenue - expenses;
    const estimatedCompanyTax = Math.max(0, estimatedProfit) * COMPANY_TAX_RATE;
    const gstCollected = entries
      .filter((item) => item.category === "Revenue")
      .reduce((total, item) => total + getGstAmount(item), 0);
    const gstClaimable = entries
      .filter((item) => item.category === "Expenses" || item.category === "Assets")
      .reduce((total, item) => total + getGstAmount(item), 0);
    const gstPosition = gstCollected - gstClaimable;

    const existing = adjustments[year.id];
    const carryOverLoss = existing?.carryOverLoss ?? (estimatedProfit < 0 ? Math.abs(estimatedProfit) : 0);
    const gstCarryOver = existing?.gstCarryOver ?? 0;
    const taxPaid = existing?.taxPaid ?? 0;
    const provisionalTaxPaid = existing?.provisionalTaxPaid ?? 0;
    const accountantAdjustments = existing?.accountantAdjustments ?? 0;
    const openingPosition = existing?.openingPosition ?? previousFinal;
    const finalPosition =
      openingPosition + estimatedCompanyTax + gstPosition + gstCarryOver - taxPaid - provisionalTaxPaid + accountantAdjustments;

    return {
      id: year.id,
      taxYear: year.taxYear,
      startDate: year.startDate,
      endDate: year.endDate,
      revenue,
      expenses,
      assetPurchases,
      estimatedProfit,
      estimatedCompanyTax,
      gstCollected,
      gstClaimable,
      gstPosition,
      openingPosition,
      carryOverLoss,
      gstCarryOver,
      taxPaid,
      provisionalTaxPaid,
      accountantAdjustments,
      finalPosition,
      notes: existing?.notes || "",
      updatedAt: Date.now(),
    } as TaxYearAdjustment;
  };

  const generated = generatedYears.reduce<TaxYearAdjustment[]>((acc, year) => {
    const previousFinal = acc[acc.length - 1]?.finalPosition || 0;
    acc.push(calculateYear(year, previousFinal));
    return acc;
  }, []);

  const saveGenerated = async () => {
    try {
      await Promise.all(
        generated.map((item) =>
          setDoc(doc(db, `taxYearAdjustments/${item.id}`), item, { merge: true }),
        ),
      );
      message.success("Tax year carry-over records generated.");
    } catch (err) {
      console.error(err);
      message.error("Could not generate tax year records.");
    }
  };

  const startEdit = (record: TaxYearAdjustment) => {
    setEditing(record.id);
    form.setFieldsValue(record);
  };

  const saveEdit = async () => {
    if (!editing) return;
    const values = await form.validateFields();
    const base = generated.find((item) => item.id === editing);
    if (!base) return;

    const updated = {
      ...base,
      ...values,
      finalPosition:
        values.openingPosition +
        base.estimatedCompanyTax +
        base.gstPosition +
        values.gstCarryOver -
        values.taxPaid -
        values.provisionalTaxPaid +
        values.accountantAdjustments,
      updatedAt: Date.now(),
    };

    await setDoc(doc(db, `taxYearAdjustments/${editing}`), updated, { merge: true });
    message.success("Tax year adjustment saved.");
    setEditing(null);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <p>
        Generate annual carry-over records from company inception. Each year can be edited for accountant adjustments,
        tax paid, GST carry-over, provisional tax paid, and opening/final position.
      </p>
      <Button type="primary" onClick={saveGenerated}>Generate / Refresh Tax Year Carry-Overs</Button>

      {editing && (
        <Form form={form} layout="vertical">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
            <Form.Item name="openingPosition" label="Opening position"><InputNumber style={{ width: "100%" }} /></Form.Item>
            <Form.Item name="carryOverLoss" label="Carry-over loss"><InputNumber min={0} style={{ width: "100%" }} /></Form.Item>
            <Form.Item name="gstCarryOver" label="GST carry-over"><InputNumber style={{ width: "100%" }} /></Form.Item>
            <Form.Item name="taxPaid" label="Tax paid"><InputNumber min={0} style={{ width: "100%" }} /></Form.Item>
            <Form.Item name="provisionalTaxPaid" label="Provisional tax paid"><InputNumber min={0} style={{ width: "100%" }} /></Form.Item>
            <Form.Item name="accountantAdjustments" label="Accountant adjustments"><InputNumber style={{ width: "100%" }} /></Form.Item>
          </div>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={3} /></Form.Item>
          <div style={{ display: "flex", gap: 8 }}>
            <Button type="primary" onClick={saveEdit}>Save Adjustment</Button>
            <Button onClick={() => setEditing(null)}>Cancel</Button>
          </div>
        </Form>
      )}

      <Table
        size="small"
        pagination={false}
        dataSource={generated.map((item) => ({ ...item, key: item.id }))}
        scroll={{ x: 1300 }}
        columns={[
          { title: "Tax Year", dataIndex: "taxYear", fixed: "left" },
          { title: "Revenue", dataIndex: "revenue", render: money },
          { title: "Expenses", dataIndex: "expenses", render: money },
          { title: "Profit Est.", dataIndex: "estimatedProfit", render: money },
          { title: "Company Tax", dataIndex: "estimatedCompanyTax", render: money },
          { title: "GST Position", dataIndex: "gstPosition", render: money },
          { title: "Opening", dataIndex: "openingPosition", render: money },
          { title: "Loss C/O", dataIndex: "carryOverLoss", render: money },
          { title: "GST C/O", dataIndex: "gstCarryOver", render: money },
          { title: "Tax Paid", dataIndex: "taxPaid", render: money },
          { title: "Prov. Paid", dataIndex: "provisionalTaxPaid", render: money },
          { title: "Acct Adj.", dataIndex: "accountantAdjustments", render: money },
          { title: "Final Position", dataIndex: "finalPosition", render: money },
          { title: "Notes", dataIndex: "notes" },
          { title: "Action", render: (_value, record) => <Button onClick={() => startEdit(record)}>Edit</Button> },
        ]}
      />
    </div>
  );
};
